import {
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CInput,
  CInputGroup,
  CInputGroupText,
  CLabel,
  CPagination,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";

import { getList } from "../../api/generics";
import SystemNotification, {
  NOTIFICATION_LEVEL_CHOICES,
  NOTIFICATION_LEVEL_COLORS,
  NOTIFICATION_SOURCE_CHOICES,
} from "../../models/system-notification";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import SystemNotificationDeleteModal from "./SystemNotificationDeleteModal";
import SystemNotificationUpdateModal from "./SystemNotificationUpdateModal";

const ITEMS_PER_PAGE = 10;

const SystemNotificationCrud = () => {
  const fields = [
    {
      key: "id",
      _classes: ["text-center"],
      label: "#",
    },
    {
      key: "level",
      _classes: ["text-center"],
      label: "Nivel",
    },
    {
      key: "description",
      _classes: ["text-center"],
      label: "Mensaje",
    },
    {
      key: "taxPayerId",
      _classes: ["text-center"],
      label: "Alcance",
    },
    {
      key: "source",
      _classes: ["text-center"],
      label: "Origen",
    },
    {
      key: "active",
      _classes: ["text-center"],
      label: "Activo",
    },
    {
      key: "createdAt",
      _classes: ["text-center"],
      label: "Creado",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [search, setSearch] = useState<string>("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notification, setNotification] = useState<
    SystemNotification | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchNotifications = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const notificationsStatus = await getList<SystemNotification>(
      "/system_notifications/admin/",
      limit,
      offset,
      urlParams
    );
    if (notificationsStatus.status === SUCCESS) {
      if (notificationsStatus.data !== undefined) {
        const count = notificationsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setNotifications(notificationsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = notificationsStatus.detail
        ? notificationsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (notification: SystemNotification) => {
    setNotification(notification);
    setShowEditModal(true);
  };

  const onDeleteClick = (notification: SystemNotification) => {
    setNotification(notification);
    setShowDeleteModal(true);
  };

  const onEditCancel = () => {
    setNotification(undefined);
    setShowEditModal(false);
  };

  const onEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setNotification(undefined);
    setShowEditModal(false);
    setNotifications([]);
    setLoading(true);
    fetchNotifications();
  };

  const onDeleteCancel = () => {
    setNotification(undefined);
    setShowDeleteModal(false);
  };

  const onDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setNotification(undefined);
    setShowDeleteModal(false);
    setNotifications([]);
    setLoading(true);
    fetchNotifications();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("systemNotifications")) {
        return;
      }
      fetchNotifications();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Avisos</h3>
              </div>
              <div className="p-2 ">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                  &nbsp; Agregar Nuevo
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={notifications}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    level: (item: SystemNotification) => {
                      return (
                        <td className="text-center">
                          <CBadge
                            color={
                              NOTIFICATION_LEVEL_COLORS.get(item.level ?? "") ??
                              "secondary"
                            }
                          >
                            {NOTIFICATION_LEVEL_CHOICES.get(item.level ?? "") ??
                              item.level}
                          </CBadge>
                        </td>
                      );
                    },
                    description: (item: SystemNotification) => {
                      return (
                        <td className="text-center">
                          {item.title ? <strong>{item.title}: </strong> : <></>}
                          {item.description}
                        </td>
                      );
                    },
                    taxPayerId: (item: SystemNotification) => {
                      return (
                        <td className="text-center">
                          {item.taxPayerId === undefined
                            ? "Global"
                            : `Contribuyente #${item.taxPayerId}`}
                        </td>
                      );
                    },
                    source: (item: SystemNotification) => {
                      return (
                        <td className="text-center">
                          {NOTIFICATION_SOURCE_CHOICES.get(item.source ?? "") ??
                            item.source}
                          {item.source === "automatic" ? (
                            <>
                              <br />
                              <small className="text-muted">{item.type}</small>
                            </>
                          ) : (
                            <></>
                          )}
                        </td>
                      );
                    },
                    active: (item: SystemNotification) => {
                      return (
                        <td className="text-center">
                          <CBadge color={item.active ? "success" : "secondary"}>
                            {item.active ? "Sí" : "No"}
                          </CBadge>
                        </td>
                      );
                    },
                    createdAt: (item: SystemNotification) => {
                      return (
                        <td className="text-center">
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString()
                            : "-"}
                        </td>
                      );
                    },
                    actions: (item: SystemNotification) => {
                      const editButton = (
                        <CButton
                          className="text-white"
                          color="warning"
                          onClick={() => {
                            onUpdateClick(item);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                      );
                      const deleteButton = (
                        <CButton
                          className="text-white"
                          color="danger"
                          onClick={() => {
                            onDeleteClick(item);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </CButton>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            {editButton}
                            {deleteButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <SystemNotificationUpdateModal
        show={showEditModal}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
        systemNotification={notification}
      />
      <SystemNotificationDeleteModal
        show={showDeleteModal}
        onCancel={onDeleteCancel}
        onSuccess={onDeleteSuccess}
        systemNotification={notification}
      />
    </>
  );
};

export default SystemNotificationCrud;
